import { Injectable } from '@angular/core'
import { PrematchService } from '@/features/betting-offer/prematch/prematch.service'
import { AssetsService } from '@/services/assets.service'
import DateTimeService from '@/services/date-time.service'
import { injectLogger } from '@/services/logger.service'
import { SearchView } from '../search.model'
import { SearchMapperContext, toSearchView } from '../search.mapper'
import { SearchSource } from '../search.source'

/** Matches and tournaments of the betting offer: what the panel searches by default. */
@Injectable({ providedIn: 'root' })
export class SportsbookSearchSource implements SearchSource {
	private readonly logger = injectLogger('SportsbookSearchSource')

	constructor(
		private prematchService: PrematchService,
		private assetsService: AssetsService,
		private dateTimeService: DateTimeService
	) {}

	// Logged as the casino calls are: the request with its payload, then the response or the error.
	public async search(term: string): Promise<SearchView> {
		const payload = { searchPattern: term }

		try {
			const response = await this.prematchService.getBofSearch(term)
			this.logger.debug('bofSearch', { payload, response })
			return toSearchView(response, this.mapperContext())
		} catch (error) {
			this.logger.error('bofSearch', { payload, error })
			throw error
		}
	}

	private mapperContext(): SearchMapperContext {
		return {
			sportIdByName: new Map(this.prematchService.sports().map((sport) => [sport.sport, sport.sportId])),
			sportImageUrl: (sportId) => this.assetsService.getSportIcon(sportId, 'color'),
			formatPlayDate: (playDate) => this.formatPlayDate(playDate)
		}
	}

	/** Kick-off time when the match is today, otherwise its date. */
	private formatPlayDate(playDate: string): string {
		return this.dateTimeService.isToday(playDate)
			? this.dateTimeService.format(playDate, {
				year: undefined, month: undefined, day: undefined, hour: '2-digit', minute: '2-digit'
			})
			: this.dateTimeService.format(playDate, {
				year: 'numeric', month: '2-digit', day: '2-digit', hour: undefined, minute: undefined
			})
	}
}
